import { ChevronRight } from 'lucide-react';
import {
  createContext,
  useContext,
  type HTMLAttributes,
  type TableHTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';

import { ROW_DIVIDER } from '@/shared/ui-class';
import { cn } from '@/shared/cn';

/** Outer bordered scroll container for admin tables. Same `--color-divider`
 * hairline as the row separators — one border vocabulary per surface
 * (solid + thin, visible in both modes). */
export const TABLE_WRAP = 'overflow-x-auto rounded-2xl border border-divider';

/** Distinct header-row background. Dark uses a white-alpha lift — the old
 * `dark:bg-zinc-900/40` resolved DARKER than the card behind it (remapped
 * zinc-900 ≈ #0c0b0a), inverting the emphasis instead of raising it. */
export const TABLE_HEADER = 'bg-zinc-50 text-left dark:bg-white/[0.04]';

/** Roomy body row with hover affordance. Row separators use the shared
 * solid hairline divider vocabulary (`ROW_DIVIDER`, ui-class.ts). */
export const TABLE_ROW = `${ROW_DIVIDER} hover:bg-zinc-50 dark:hover:bg-[color-mix(in_srgb,var(--color-zinc-900)_30%,transparent)]`;

/** Standard cell padding (taller rows). */
export const TABLE_CELL = 'px-4 py-3';

const TableContext = createContext<{ clickable?: boolean }>({});
/** True for rows inside <THead>, so the clickable-chevron column renders a
 * matching empty <th> in the header (keeps column counts equal). */
const HeaderContext = createContext(false);

export type TableProps = TableHTMLAttributes<HTMLTableElement> & {
  /** When true, body rows with `onRowClick` append a trailing chevron column. */
  clickableRows?: boolean;
  /** Wrap the table in the bordered container. Default true. */
  wrap?: boolean;
};

export function Table({
  className,
  clickableRows,
  wrap = true,
  children,
  ...rest
}: TableProps) {
  const table = (
    <TableContext.Provider value={{ clickable: clickableRows }}>
      <table className={cn('w-full text-sm', className)} {...rest}>
        {children}
      </table>
    </TableContext.Provider>
  );

  if (!wrap) return <div className="overflow-x-auto">{table}</div>;

  return <div className={TABLE_WRAP}>{table}</div>;
}

export function THead({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead className={cn(TABLE_HEADER, className)} {...rest}>
      <HeaderContext.Provider value={true}>{children}</HeaderContext.Provider>
    </thead>
  );
}

export function TBody({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody className={className} {...rest}>
      {children}
    </tbody>
  );
}

export type TRProps = HTMLAttributes<HTMLTableRowElement> & {
  onRowClick?: () => void;
};

export function TR({ className, onRowClick, children, ...rest }: TRProps) {
  const { clickable } = useContext(TableContext);
  const isHeader = useContext(HeaderContext);

  const trailingCell = clickable
    ? isHeader
      ? <th className={cn(TABLE_CELL, 'w-10')} aria-hidden />
      : (
          <td className={cn(TABLE_CELL, 'w-10 text-right text-zinc-400')}>
            {onRowClick ? (
              <ChevronRight className="inline h-4 w-4" aria-hidden />
            ) : null}
          </td>
        )
    : null;

  return (
    <tr
      className={cn(
        TABLE_ROW,
        onRowClick && 'cursor-pointer',
        className,
      )}
      onClick={onRowClick}
      {...rest}
    >
      {children}
      {trailingCell}
    </tr>
  );
}

export function TH({
  className,
  children,
  ...rest
}: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={cn(
        TABLE_CELL,
        'text-xs font-medium uppercase tracking-wide text-muted',
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  );
}

export function TD({
  className,
  children,
  ...rest
}: TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td className={cn(TABLE_CELL, className)} {...rest}>
      {children}
    </td>
  );
}
